/* ===================================================================
   WAVE dispatch property. 17 September 2026.

   WHAT PROBLEM THIS SOLVES
   The Shopify checkout order summary is not part of the theme. None of
   WAVE's dispatch banners can reach it, so at the exact moment a customer
   commits they see a product name, a price and nothing about when it will
   be sent. The one thing a theme CAN put in that summary is a line item
   property, which Shopify prints under the product name at checkout, on
   the order in the Shopify admin and on the packing slip.

   WHAT IT ATTACHES TO EVERY BASKET LINE
     Estimated dispatch   Shown at checkout. For example
                          "Thursday 2 October 2026 (made to order)"
                          or "Thursday 18 September 2026".
                          Hidden inside the basket on purpose, see below.
     _wave_mto            Hidden everywhere. The leading underscore is what
                          hides it from both the basket and the checkout.
                          "1" or "0", so the line's dispatch wording can be
                          checked later without re-deriving the stock state.

   ONE DATE FOR THE WHOLE ORDER. 20 October 2026.
   The basket promises "your whole order ships together" on one date, chosen
   on 14 September. Until today the checkout contradicted it: a basket with
   one in stock item and one made to order item printed two different
   dispatch dates, one per line, so a customer about to pay read "21 October"
   on one product and "4 November" on the other and could not tell which was
   true. If ANY line is made to order the whole parcel waits, so every line
   now carries the made to order date and the checkout agrees with the
   basket. See anyMto in part two.

   IT WORKS IN TWO HALVES

   1. ON THE PRODUCT PAGE
      Two hidden fields are added to the add to cart form, so the property
      is on the line from the moment it is added. The fields are created
      here rather than in Liquid, which keeps snippets/product-form.liquid
      (31KB, and the most load bearing file on the site) untouched. The
      values follow the shade and length the customer picks. A product page
      shows one product, so these fields use that product's own state. Where
      that disagrees with the rest of the basket, part two corrects it on the
      next pass, which runs about 900ms after the item is added.

   2. ON EVERY PAGE LOAD
      A line item property is frozen at the moment the item is added. But
      WAVE's dispatch date is counted from the day the customer ORDERS, not
      the day they added it. A basket left for a week would carry a date
      several days too early, which is a broken promise printed at the worst
      possible moment. So every line is checked against what it should say
      today, and only lines that have drifted are rewritten. Almost always
      nothing has drifted and nothing is written.

   THE ONE REMAINING GAP
   A basket abandonment email that links straight to /checkouts/... never
   loads a storefront page, so nothing here runs and the date can be stale.
   Point those emails at /cart instead and the gap closes.

   WHERE THE MADE TO ORDER FLAG COMES FROM
   It cannot be worked out in the browser. Shopify's /cart.js carries
   "available" but never "inventory_quantity", and neither does
   /products/HANDLE.js. It has to come from Liquid. Two places already do it:
     product page  snippets/wave-variant-state.liquid, data-preorder, which
                   is re-rendered every time a swatch is clicked
     basket        snippets/cart-item.liquid, data-wave-mto, worked out from
                   live inventory every time the drawer renders, and that
                   drawer markup sits in the page on every page load even
                   when it is closed
   Both are read here. Neither is guessed at.

   WHY THE PROPERTY IS HIDDEN INSIDE THE BASKET
   The basket already shows one dispatch message for the whole order, chosen
   on 14 September. A per item line underneath it would say the same thing
   twice. snippets/cart-item.liquid and snippets/cart-item-page.liquid skip
   the property by name. Shopify's checkout is not the theme, so it still
   shows it there, which is the whole point.

   THE TWO LEAD TIMES AND THE WORDING LIVE IN ONE PLACE
   snippets/wave-dispatch-dates.liquid. Nothing in this file hard codes a
   number of days or a form of words. If you need to change either, change
   it there and this file follows.
   ==================================================================== */
(function () {
  'use strict';

  if (window.WAVE_DISPATCH_PROP_READY) { return; }
  window.WAVE_DISPATCH_PROP_READY = true;

  /* The theme editor re-renders sections constantly and writing to the
     basket from inside it is confusing for whoever is editing. */
  if (window.Shopify && window.Shopify.designMode) { return; }

  var API = window.WAVE_DISPATCH_PROP;
  if (!API || typeof API.value !== 'function') { return; }

  var NAME = API.name;
  var FLAG = API.flag;

  var NAME_FIELD = 'properties[' + NAME + ']';
  var FLAG_FIELD = 'properties[' + FLAG + ']';

  function list(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  /* ===================================================================
     PART ONE. The add to cart form.
     =================================================================== */

  function setHiddenField(form, fieldName, value) {
    var input = form.querySelector('input[type="hidden"][name="' + fieldName + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('name', fieldName);
      input.setAttribute('data-wave-dispatch-prop', '');
      form.appendChild(input);
    }
    if (input.value !== value) { input.value = value; }
  }

  function paintForms() {
    var forms = list(document, 'form[data-type="add-to-cart-form"]');
    var i;

    for (i = 0; i < forms.length; i++) {
      var form = forms[i];
      var state = form.querySelector('[data-wave-variant-state]');

      /* Quick view and frequently bought together render the form outside the
         block that holds the variant state, so look just outside it too. */
      if (!state && form.parentElement) {
        var scope = form.closest
          ? form.closest('[data-product-featured], .t4s-product__info, .t4s-product-wrapper, .t4s-product')
          : null;
        if (scope) { state = scope.querySelector('[data-wave-variant-state]'); }
      }

      /* No variant state means no trustworthy way to tell in stock from made
         to order. Attaching a guess would be worse than attaching nothing.
         Part two picks these lines up on the next page load. */
      if (!state) { continue; }

      var mto = state.getAttribute('data-preorder') === 'true';
      setHiddenField(form, NAME_FIELD, API.value(mto));
      setHiddenField(form, FLAG_FIELD, mto ? '1' : '0');
    }
  }

  var paintTimer = null;
  function paintFormsSoon() {
    if (paintTimer) { window.clearTimeout(paintTimer); }
    paintTimer = window.setTimeout(paintForms, 120);
  }

  /* ===================================================================
     PART TWO. Keeping the basket honest.
     =================================================================== */

  /* Each pass either changes nothing or brings every line up to date, so a
     second pass finds nothing to do. The cap is only here so that a future
     disagreement between two bits of code can never become a loop. */
  var passesLeft = 3;

  function getJSON(url, options) {
    return fetch(url, options).then(function (r) {
      return r.ok ? r.json() : null;
    })['catch'](function () { return null; });
  }

  function readFlagNodes(nodes) {
    return Array.prototype.map.call(nodes, function (n) {
      return {
        mto: n.getAttribute('data-wave-mto') === 'true',
        pid: n.getAttribute('data-pid') || ''
      };
    });
  }

  /* The drawer and the /cart page both mark their lines with data-wave-mto.
     On the /cart page BOTH are present, so the drawer is taken first and the
     page table is only a fallback, otherwise every line is counted twice. */
  function flagsFromDocument() {
    var nodes = document.querySelectorAll('.t4s-mini_cart__item[data-cart-item][data-wave-mto]');
    if (!nodes.length) {
      nodes = document.querySelectorAll('tr[data-cart-item][data-wave-mto]');
    }
    return readFlagNodes(nodes);
  }

  /* Belt and braces. If a template ever stops carrying the drawer, ask
     Shopify to render just that section and read the flags out of the HTML. */
  function flagsFromSection() {
    return fetch('/?section_id=mini_cart').then(function (r) {
      return r.ok ? r.text() : null;
    }).then(function (html) {
      if (!html) { return []; }
      var holder = document.createElement('div');
      holder.innerHTML = html;
      return readFlagNodes(holder.querySelectorAll('[data-cart-item][data-wave-mto]'));
    })['catch'](function () { return []; });
  }

  function flags() {
    var fromDoc = flagsFromDocument();
    if (fromDoc.length) { return Promise.resolve(fromDoc); }
    return flagsFromSection();
  }

  /* Shopify keys a basket line on the variant AND its properties, so a line
     whose properties change gets a new key and the basket is renumbered.
     Every change is therefore looked up again against a freshly fetched
     basket rather than against a remembered position. */
  function applyOne(job) {
    return getJSON('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (cart) {
        if (!cart || !cart.items) { return null; }
        var i;
        for (i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === job.key) {
            return getJSON('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ id: job.key, properties: job.properties })
            });
          }
        }
        /* The line has gone, or has merged into another one. Nothing to do. */
        return null;
      });
  }

  function tellTheTheme() {
    var names = ['cart:refresh', 'cart:build'];
    var i;
    for (i = 0; i < names.length; i++) {
      try {
        document.dispatchEvent(new CustomEvent(names[i], { bubbles: true }));
      } catch (e) { /* An old browser. The basket is still correct. */ }
    }
  }

  function refreshBasket() {
    if (passesLeft <= 0) { return; }
    passesLeft--;

    getJSON('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) { return; }

        return flags().then(function (lineFlags) {
          /* If the two lists are different lengths they cannot be lined up
             safely, and guessing would write the wrong date onto the wrong
             product. Do nothing at all, and let the next page load fix it. */
          if (lineFlags.length !== cart.items.length) { return; }

          var jobs = [];
          var i, k;

          /* ONE DATE FOR THE WHOLE ORDER. The basket promises that the whole
             order ships together, so the slowest line sets the date for all
             of them. One made to order line means the parcel waits, and
             every line at checkout says so. Without this the checkout
             printed a different date on each line and contradicted the
             basket at the moment of payment. */
          var anyMto = false;
          for (i = 0; i < lineFlags.length; i++) {
            if (lineFlags[i].mto) { anyMto = true; break; }
          }

          for (i = 0; i < cart.items.length; i++) {
            var item = cart.items[i];
            var f = lineFlags[i];

            /* The drawer carries the product id. Where it does, use it to
               prove the two lists really are in the same order. */
            if (f.pid && String(f.pid) !== String(item.product_id)) { return; }

            var wantValue = API.value(anyMto);
            var wantFlag = anyMto ? '1' : '0';
            var current = item.properties || {};

            if (current[NAME] === wantValue && current[FLAG] === wantFlag) {
              continue;
            }

            /* Copy anything else already on the line so nothing is lost. */
            var props = {};
            for (k in current) {
              if (Object.prototype.hasOwnProperty.call(current, k)) {
                props[k] = current[k];
              }
            }
            props[NAME] = wantValue;
            props[FLAG] = wantFlag;

            jobs.push({ key: item.key, properties: props });
          }

          if (!jobs.length) { return; }

          /* One at a time, in order. */
          var chain = Promise.resolve();
          jobs.forEach(function (job) {
            chain = chain.then(function () { return applyOne(job); });
          });
          return chain.then(tellTheTheme);
        });
      })['catch'](function () { /* Never let this break a page. */ });
  }

  var refreshTimer = null;
  function refreshBasketSoon(delay) {
    if (refreshTimer) { window.clearTimeout(refreshTimer); }
    refreshTimer = window.setTimeout(refreshBasket, delay || 800);
  }

  /* ===================================================================
     Wiring.
     =================================================================== */

  function start() {
    paintForms();
    refreshBasket();

    /* The variant picker replaces the variant state block through the Section
       Rendering API, and quick view injects whole forms after the fact, so
       watch for both rather than only painting once. */
    if (window.MutationObserver) {
      new MutationObserver(paintFormsSoon).observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    /* snippets/product-form.liquid fires this on the form itself when the
       shade or length changes. Captured at the document so it is caught
       whether or not it bubbles. */
    document.addEventListener('wave:variant:changed', paintFormsSoon, true);

    /* A line added by something that had no variant state to read, for
       example quick view, arrives with no property. Catch it once the drawer
       has re-rendered with the new line in it. */
    document.addEventListener('cart:added', function () {
      refreshBasketSoon(900);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();